import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { useTaskStore } from '@/store/useTaskStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

export function RunSummary() {
  const { activeRunId } = useTaskStore()
  const [isExpanded, setIsExpanded] = useState(true)

  const { data, isLoading } = useQuery({
    queryKey: ['run-summary', activeRunId],
    queryFn: () => api.getRunSummary(activeRunId!),
    enabled: !!activeRunId,
    refetchInterval: 3000,
  })

  if (!activeRunId) {
    return null
  }

  const isRunning = data?.status === 'running'
  const isComplete = data?.status === 'done' || data?.status === 'completed'

  // Don't show summary until pipeline is complete
  if (isRunning || isLoading) {
    return null
  }

  // Only show if complete
  if (!isComplete) {
    return null
  }

  const metrics = [
    {
      label: 'Sales Nav Results',
      value: data?.outputs?.sales_nav?.total_results || 0,
    },
    {
      label: 'Unique Engagers',
      value: data?.outputs?.engagers?.unique_engagers || 0,
    },
    {
      label: 'Posts Analyzed',
      value: data?.outputs?.engagers?.posts_analyzed || 0,
    },
    {
      label: 'Profiles Enriched',
      value: data?.outputs?.profiles?.profiles_enriched || 0,
    },
    {
      label: 'Post Matches',
      value: data?.outputs?.posts?.profiles_with_relevant_content || 0,
    },
    {
      label: 'Final Count',
      value: data?.outputs?.final_count || 0,
      highlight: true,
    },
  ]

  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none hover:bg-slate-900"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
          )}
          <CardTitle>Run Summary</CardTitle>
          {!isExpanded && data?.outputs?.final_count != null && (
            <span className="ml-auto text-xs text-green-400 font-mono">
              {data.outputs.final_count} candidates
            </span>
          )}
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className={
                metric.highlight
                  ? 'p-3 rounded-md bg-slate-800 border border-slate-600'
                  : 'p-3 rounded-md border border-slate-800 bg-slate-950/30'
              }
            >
              <p className="text-xs text-slate-400 mb-1">
                {metric.label}
              </p>
              <p className="text-xl font-mono font-semibold tabular-nums">{metric.value}</p>
            </div>
          ))}
        </div>
      </CardContent>
      )}
    </Card>
  )
}
